import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  ConfirmationPopup,
  ContentLoader,
  IconCheckmark,
  IconDisabled,
  List,
  Notification,
  StatusCard,
} from 'shared/components';
import {
  displayValue,
  formatDate,
  getCurrentUserPermissions,
  getLicenseStatus,
  hasPermission,
} from 'shared/helpers';
import {
  LicenseUserMaxActivations,
  LicenseUsersActionMenu,
  RemoveLicenseUserForm,
} from 'licenses/components';
import { removeLicenseUser } from 'licenses/components/RemoveLicenseUserForm/actions';
import { setLicenseUserActivations } from 'licenses/LicenseContainer/LicenseUsers/actions';
import {
  fetchLicense,
  fetchLicensesByLicenseUser,
} from '../../licenses/actions';

const PAGE_SIZE = 10;

// The license list has no per-user activation counts; the license detail has
// them for each assigned user, so load the details of the licenses on the page.
const fetchUserActivations = async (licenses, licenseUserId) => {
  const responses = await Promise.allSettled(
    licenses.map((license) => fetchLicense(license.id)),
  );
  return responses.reduce((acc, response, index) => {
    if (response.status !== 'fulfilled') {
      return acc;
    }
    const assignedUser = get(response, 'value.data.license_users', []).find(
      (user) => String(user.id) === String(licenseUserId),
    );
    if (assignedUser) {
      acc[licenses[index].id] = {
        max_activations: assignedUser.max_activations,
        total_activations: assignedUser.total_activations,
      };
    }
    return acc;
  }, {});
};

const LicenseUserLicenses = ({ licenseUserId, licenseUserEmail }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const permissionsList = useSelector((state) =>
    getCurrentUserPermissions(state),
  );
  const canManageLicenseUsers = hasPermission(
    permissionsList,
    'manage_license_users',
  );
  const [licenses, setLicenses] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [activationsByLicense, setActivationsByLicense] = useState({});
  const [selectedLicenseIDs, setSelectedLicenseIDs] = useState([]);
  const [licenseToEdit, setLicenseToEdit] = useState(null);
  const [activeAction, setActiveAction] = useState(null);
  const [isActionLoading, setActionLoading] = useState(false);

  const getLicenses = useCallback(
    async (page, sort) => {
      setIsLoading(true);
      try {
        const response = await fetchLicensesByLicenseUser(
          licenseUserId,
          sort,
          page,
          PAGE_SIZE,
        );
        const results = get(response, 'data.results', []);
        const total = get(response, 'data.count', 0);
        const lastPage = Math.max(Math.ceil(total / PAGE_SIZE) - 1, 0);
        if (!results.length && page > lastPage) {
          // The user was removed from the last licenses on this page
          setCurrentPage(lastPage);
          return;
        }
        setLicenses(results);
        setCount(total);
        setSelectedLicenseIDs((selected) =>
          selected.filter((id) => results.some((license) => license.id === id)),
        );
        setActivationsByLicense(
          await fetchUserActivations(results, licenseUserId),
        );
      } catch (error) {
        Notification(
          'error',
          t('Error occurred'),
          t('There was an error while getting licenses'),
        );
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [licenseUserId],
  );

  useEffect(() => {
    if (licenseUserId) {
      getLicenses(currentPage, currentSort);
    }
  }, [licenseUserId, currentPage, currentSort, getLicenses, retryToken]);

  const handleSortChange = (newSort) => {
    setSelectedLicenseIDs([]);
    setCurrentSort(newSort);
  };

  const handlePageChange = (page) => {
    setSelectedLicenseIDs([]);
    setCurrentPage(page);
  };

  const handleRowClick = (row) => {
    const licenseId = get(row, 'id');
    if (licenseId) {
      history.push(`/licenses/${licenseId}`);
    }
  };

  const handleRetry = () => {
    setRetryToken((token) => token + 1);
    setError(false);
  };

  const refreshLicenses = () => setRetryToken((token) => token + 1);

  const closeAction = () => {
    setActiveAction(null);
    setLicenseToEdit(null);
  };

  const openAction = (action, license) => {
    if (!canManageLicenseUsers) {
      Notification(
        'error',
        t('Missing permission'),
        t('You do not have permission to perform this action.'),
      );
      return;
    }
    setLicenseToEdit(license);
    setActiveAction(action);
  };

  const toggleLicenseSelection = (licenseID) => {
    setSelectedLicenseIDs((selected) =>
      selected.includes(licenseID)
        ? selected.filter((id) => id !== licenseID)
        : selected.concat(licenseID),
    );
  };

  const resetTotalActivations = () => {
    setActionLoading(true);
    setLicenseUserActivations(get(licenseToEdit, 'id'), {
      [licenseUserId]: { reset_total_activations: true },
    })
      .then(() => {
        Notification('success', t('Changes saved successfully'), '');
        closeAction();
        refreshLicenses();
      })
      .catch(() => {
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
      })
      .finally(() => setActionLoading(false));
  };

  const removeFromSelectedLicenses = async () => {
    setActionLoading(true);
    const failed = [];

    for (const licenseID of selectedLicenseIDs) {
      try {
        await removeLicenseUser(licenseID, licenseUserId);
      } catch (err) {
        failed.push(licenseID);
      }
    }

    setActionLoading(false);
    setSelectedLicenseIDs(failed);
    closeAction();
    refreshLicenses();

    if (failed.length) {
      Notification(
        'error',
        t('Some licenses were not updated'),
        t('There was an error while saving your changes'),
      );
    } else {
      Notification(
        'success',
        t('Changes saved successfully'),
        t('User has been removed from the selected licenses'),
      );
    }
  };

  const selectedLicensesLabel = licenses
    .filter((license) => selectedLicenseIDs.includes(license.id))
    .map((license) => get(license, 'product.product_name'))
    .join(', ');

  const columns = [
    {
      id: 'select',
      className: 'select-container',
      Cell: (rowData) => (
        <button
          className="table-select"
          aria-label="Select license"
          onClick={(e) => {
            e.stopPropagation();
            toggleLicenseSelection(rowData.original.id);
          }}
          type="button"
        >
          <Checkbox
            inputId={`${rowData.original.id}-select`}
            checked={selectedLicenseIDs.includes(rowData.original.id)}
          />
        </button>
      ),
      width: 60,
      show: canManageLicenseUsers,
      sortable: false,
    },
    {
      Header: t('Created on'),
      accessor: 'created_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 110,
    },
    {
      Header: t('License key'),
      id: 'license_key',
      accessor: (row) =>
        get(row, 'license_key') ?? get(row, 'licenseKey') ?? null,
      Cell: (cellData) => displayValue(cellData.value),
      width: 160,
    },
    {
      Header: t('Product'),
      accessor: 'product.product_name',
      Cell: (cellData) => displayValue(cellData.value),
      sortable: false,
    },
    {
      Header: t('License Type'),
      accessor: 'license_type',
      Cell: (rowData) => {
        const isBundle = get(rowData, 'original.is_bundle');
        if (isBundle) return '-';
        return displayValue(t(rowData.value));
      },
      width: 100,
      sortable: false,
    },
    {
      Header: t('Expiration Date'),
      id: 'expiration_date',
      accessor: (row) =>
        get(row, 'expiration_date') ??
        get(row, 'expires_at') ??
        get(row, 'valid_until') ??
        get(row, 'maintenance_period') ??
        null,
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 120,
      sortable: false,
    },
    {
      Header: t('Times activated'),
      accessor: 'times_activated',
      className: 'text-center',
      headerClassName: 'text-center',
      Cell: (rowData) => {
        const isBundle = get(rowData, 'original.is_bundle');
        if (isBundle) return '-';
        return displayValue(rowData.value, 0);
      },
      width: 110,
    },
    {
      Header: t('Is trial'),
      accessor: 'is_trial',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (cellData) =>
        cellData.value ? (
          <IconCheckmark color="#10ac84" height="14px" />
        ) : (
          <IconDisabled color="#aaa" height="14px" />
        ),
      width: 70,
      sortable: false,
    },
    {
      Header: t('Status'),
      id: 'status',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (rowData) => {
        const isBundle = get(rowData, 'original.is_bundle');
        if (isBundle) return '-';
        const { status, message } = getLicenseStatus(rowData, t);
        return <StatusCard status={status} text={message} />;
      },
      width: 130,
      sortable: false,
    },
    {
      Header: t('User activations'),
      id: 'user_activations',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (rowData) => {
        const activations = get(activationsByLicense, rowData.original.id);
        if (!activations) return '-';
        return `${displayValue(activations.total_activations, 0)} / ${displayValue(activations.max_activations)}`;
      },
      width: 120,
      sortable: false,
    },
    {
      id: 'actions',
      Header: t('Actions'),
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (rowData) => (
        <LicenseUsersActionMenu
          user={{ id: licenseUserId, true_email: licenseUserEmail }}
          headerLabel={t('License')}
          headerText={get(rowData, 'original.product.product_name')}
          handleMaxActivationsChange={() =>
            openAction('max_activations', rowData.original)
          }
          handleResetTotalActivations={() =>
            openAction('reset_activations', rowData.original)
          }
          handleRemoveLicenseUser={() => openAction('remove', rowData.original)}
        />
      ),
      width: 70,
      show: canManageLicenseUsers,
      sortable: false,
    },
  ];
  if (isLoading) {
    return <ContentLoader text={t('Loading licenses')} />;
  }

  if (error) {
    return (
      <div className="LicenseUserDetails-error">
        <p>{t('Unable to load licenses for this user')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!licenses.length) {
    return (
      <div className="LicenseUserDetails-empty">
        {t('No licenses for this user')}
      </div>
    );
  }

  return (
    <div className="LicenseUserDetails-list">
      {canManageLicenseUsers && (
        <div className="LicenseUserDetails-listActions">
          <Button
            size="sm"
            theme="default"
            disabled={!selectedLicenseIDs.length}
            onClick={() => openAction('remove_selected', null)}
          >
            {t('Remove from selected licenses')}
          </Button>
        </div>
      )}
      <List
        data={licenses}
        columns={columns}
        minRows={2}
        loading={isLoading}
        manual
        defaultSorted={currentSort}
        onSortedChange={handleSortChange}
        page={currentPage}
        pages={Math.ceil(count / PAGE_SIZE)}
        showPagination={count > PAGE_SIZE}
        onPageChange={handlePageChange}
        clickable
        getTrProps={(state, rowInfo) => ({
          onClick: () => handleRowClick(rowInfo.original),
          style: { cursor: 'pointer' },
        })}
      />
      {activeAction === 'max_activations' && (
        <LicenseUserMaxActivations
          closeCb={closeAction}
          user={{
            id: licenseUserId,
            true_email: licenseUserEmail,
            max_activations: get(activationsByLicense, [
              get(licenseToEdit, 'id'),
              'max_activations',
            ]),
          }}
          licenseID={get(licenseToEdit, 'id')}
          refetchLicense={refreshLicenses}
        />
      )}
      {activeAction === 'reset_activations' && (
        <ConfirmationPopup
          closeCb={closeAction}
          confirmCb={resetTotalActivations}
          title={t(
            'Are you sure you want to reset this users total license activations count?',
          )}
          confirmText={t('Reset')}
          theme="error"
          disabled={isActionLoading}
        >
          {get(licenseToEdit, 'product.product_name')}
        </ConfirmationPopup>
      )}
      {activeAction === 'remove' && (
        <RemoveLicenseUserForm
          closeCb={closeAction}
          confirmCb={() => {
            closeAction();
            refreshLicenses();
          }}
          license={licenseToEdit}
          userToRemove={{ id: licenseUserId, true_email: licenseUserEmail }}
        />
      )}
      {activeAction === 'remove_selected' && (
        <ConfirmationPopup
          closeCb={closeAction}
          confirmCb={removeFromSelectedLicenses}
          title={t(
            'Are you sure you want to remove this user from the selected licenses?',
          )}
          confirmText={t('Remove')}
          theme="error"
          disabled={isActionLoading}
        >
          {selectedLicensesLabel}
        </ConfirmationPopup>
      )}
    </div>
  );
};

LicenseUserLicenses.propTypes = {
  licenseUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  licenseUserEmail: PropTypes.string,
};

LicenseUserLicenses.defaultProps = {
  licenseUserEmail: '',
};

export default LicenseUserLicenses;
